/**
 * Date helpers for the accreditation spreadsheet grid. Pure functions — no Vue,
 * no DOM — so the coercion/formatting rules can be unit-tested in isolation and
 * reused by any grid that stores ISO (YYYY-MM-DD) dates.
 */

/**
 * Coerce arbitrary pasted/typed text to a YYYY-MM-DD date (or null for blank).
 * `ok: false` means "leave the cell untouched" — invalid tokens are skipped,
 * never saved. Accepts an already-ISO string or any 8-digit run (e.g. 20301231).
 */
export function normalizeDate(raw: string): { ok: boolean; value: string | null } {
  const t = raw.trim();
  if (t === '') return { ok: true, value: null };
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return { ok: true, value: t };
  const digits = t.replace(/\D/g, '');
  if (digits.length === 8) {
    return { ok: true, value: `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` };
  }
  return { ok: false, value: null };
}

/**
 * Progressively format raw input as YYYY-MM-DD as the user types, inserting the
 * dashes once enough digits are present. Non-digits are dropped; capped at 8
 * digits (a full date).
 */
export function formatDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let out = digits.slice(0, 4);
  if (digits.length > 4) out += '-' + digits.slice(4, 6);
  if (digits.length > 6) out += '-' + digits.slice(6, 8);
  return out;
}

/**
 * A cell value is expired when it's a non-empty date string strictly before
 * `today` (both ISO, so a lexical compare is a date compare).
 */
export function isExpired(value: unknown, today: string): boolean {
  return typeof value === 'string' && value !== '' && value < today;
}
